// Preliminary Capital Readiness Score from Steps 1–4 of the Get Started quiz
// (post-revenue only). Mirrors the report methodology at a coarse level:
// raise-size vs revenue is the dominant signal, then profitability, then how
// fundable the capital use is. The full report replaces this once docs are in.

const REVENUE_MID_KES = { lt_100k: 50_000, "100k_1m": 500_000, "1m_5m": 3_000_000, "5m_15m": 10_000_000, gt_15m: 20_000_000 };
const RAISE_MID_KES = {
  under_1m: 500_000, "1m_5m": 3_000_000, "5m_15m": 10_000_000, "15m_25m": 20_000_000,
  "25m_50m": 37_500_000, "50m_100m": 75_000_000, "100m_500m": 300_000_000, over_500m: 750_000_000,
};
const REVENUE_SCALE_ADJ = { lt_100k: -6, "100k_1m": -2, "1m_5m": 2, "5m_15m": 4, gt_15m: 6 };
// How readily funders back each capital use (secured / self-liquidating uses score higher).
const PURPOSE_ADJ = {
  invoice_financing: 5, trade_finance: 4, asset_finance: 4, working_capital: 2,
  growth_capital: 0, other: -2, bridge_financing: -3, debt_refinancing: -6,
};

const ratioBase = (r) => (
  r <= 0.25 ? 78 : r <= 0.5 ? 68 : r <= 1 ? 58 : r <= 2 ? 48 : r <= 4 ? 38 : r <= 8 ? 30 : r <= 16 ? 22 : 14
);

export const SCORE_BANDS = [
  { id: "not_yet", min: 0, max: 30, label: "Not Yet Ready", color: "#B98A3C", ring: "#B98A3C", celebrate: 0,
    message: "You are earlier in the journey — and that is okay. Your full report will show you exactly what to work on to get there." },
  { id: "early", min: 31, max: 50, label: "Early Stage", color: "#F59E0B", ring: "#F59E0B", celebrate: 0,
    message: "You are earlier in the journey — and that is okay. Your full report will show you exactly what to work on to get there." },
  { id: "momentum", min: 51, max: 70, label: "Building Momentum", color: "#A3C43A", ring: "#A3C43A", celebrate: 0,
    message: "You're building real momentum. A few targeted improvements could move you into a strong position." },
  { id: "strong", min: 71, max: 85, label: "Strong Position", color: "#22C55E", ring: "#22C55E", celebrate: 1,
    message: "This is a strong result. Funders in our network are likely to take your business seriously." },
  { id: "ready", min: 86, max: 100, label: "Capital Ready", color: "#4ADE80", ring: "#4ADE80", celebrate: 2,
    message: "Outstanding. Your business is in a strong position to attract capital. Clinq is ready to make introductions." },
];

export const bandForScore = (s) => SCORE_BANDS.find((b) => s >= b.min && s <= b.max) || SCORE_BANDS[0];

export function computePreliminaryScore({ revenueBand, raiseBracket, isProfitable, purposes = [], primaryPurpose = "" }) {
  const rev = REVENUE_MID_KES[revenueBand];
  const raise = RAISE_MID_KES[raiseBracket];
  if (!rev || !raise) return null;
  const ratio = raise / (rev * 12);
  let score = ratioBase(ratio);
  score += REVENUE_SCALE_ADJ[revenueBand] || 0;
  score += isProfitable === true ? 6 : isProfitable === false ? -4 : 0;
  const primary = primaryPurpose && purposes.includes(primaryPurpose) ? primaryPurpose : purposes[0];
  if (primary) {
    const others = purposes.filter((p) => p !== primary);
    const secondary = others.length ? others.reduce((a, p) => a + (PURPOSE_ADJ[p] ?? 0), 0) / others.length : 0;
    score += Math.round((PURPOSE_ADJ[primary] ?? 0) * 0.7 + secondary * 0.3);
  }
  // Hard rule: sub-100K/month founders asking for 100M+ must land under 25.
  if (revenueBand === "lt_100k" && (raiseBracket === "100m_500m" || raiseBracket === "over_500m")) score = Math.min(score, 22);
  score = Math.max(8, Math.min(96, Math.round(score)));
  return { score, band: bandForScore(score), ratio };
}
